import axios from "axios";
import { CurrentUser } from "../../../hooks/local-storage/use-current-user";
import { getXtenantId } from "../../../utils/utils";

/**
 * This function is used to log errors to the Google Chat.
 * It uses the Google Chat API to log the errors.
 *
 * @param payload - The payload to be logged.
 * @returns The response from the Google Chat.
 *
 * @example
 * useLoggerExc({
 *   Environment: "env",
 *   Tenant: "abc",
 *   Exception: "abc",
 *   text: "hello world",
 *   Path: "at component",
 *   Timestamp: new Date().toISOString(),
 * });
 *
 * @author - [Rajkumar Rathod]
 */
const useLoggerExc = <T>(payload: T) => {
  const env = import.meta.env.VITE_ENV_NAME;
  if (env !== "dev") {
    const url = `https://chat.googleapis.com/v1/spaces/AAQAC-JZq2g/messages?key=${
      import.meta.env.VITE_GOOGLE_CHAT_KEY
    }&token=${import.meta.env.VITE_GOOGLE_CHAT_TOKEN}`;

    return axios({
      method: "post",
      url,
      data: { text: JSON.stringify(payload) },
      headers: {
        "Content-Type": "application/json",
      },
      /**
       * Reporting a failure must never itself become an unhandled rejection on
       * top of the failure being reported. The webhook 400s whenever the Chat
       * key/token are missing or stale for the environment, which is a logging
       * problem and not something the user can act on.
       */
    }).catch((err) => {
      console.error("Failed to log error to Google Chat:", err);
    });
  }
};

export const ErrorBoundaryLogHelper = (error: Error) => {
  const env = import.meta.env.VITE_ENV_NAME;
  const tenant = getXtenantId();

  // Read the current user straight from localStorage instead of the
  // useCurrentUser() hook, so this helper is a plain function and can be
  // safely called outside React's render phase (event handlers, Formik
  // onSubmit, error boundaries, etc.) without triggering "Invalid hook call".
  let user: CurrentUser | null = null;
  try {
    const rawUser = sessionStorage.getItem("user");
    user = rawUser ? (JSON.parse(rawUser) as CurrentUser) : null;
  } catch {
    user = null;
  }

  const payload = {
    type: "FrontEnd Exception",
    message: error?.message,
    tenant: tenant,
    user: user,
    userAgent: navigator?.userAgent,
    environment: env,
    url: window.location.href,
    timeStamp: new Date().toISOString(),
    stack: error?.stack,
  };

  useLoggerExc(payload);
};
